<div class="modal-header">
  <h2 class="modal-title fw-bold">{{ 'editRegistration.title' | translate }}</h2>
  <button type="button" class="btn-close" (click)="close()"></button>
</div>

<div class="modal-body">
  <form [formGroup]="form">
    <!-- Current Registration Info -->
    <div class="alert alert-info d-flex align-items-start mb-6">
      <i class="fa fa-info-circle fs-2x me-3 mt-1"></i>
      <div>
        <div class="fw-bold mb-2">{{ 'editRegistration.currentRegistration' | translate }}</div>
        <div class="mb-1">
          <strong>{{ 'editRegistration.rider' | translate }}:</strong>
          @if (registration.user) {
            {{ registration.user.first_name }} {{ registration.user.last_name }}
            @if (registration.user.email) {
              <span class="text-muted">({{ registration.user.email }})</span>
            }
          } @else {
            <span class="text-muted">{{ 'editRegistration.na' | translate }}</span>
          }
        </div>
        <div>
          <strong>{{ 'editRegistration.horse' | translate }}:</strong>
          @if (registration.horse) {
            {{ registration.horse.name }}
            @if (registration.horse.breed) {
              <span class="text-muted">({{ registration.horse.breed }})</span>
            }
          } @else {
            <span class="text-muted">{{ 'editRegistration.na' | translate }}</span>
          }
        </div>
      </div>
    </div>

    <!-- User Search Field -->
    <div class="mb-5">
      <label class="form-label required">{{ 'editRegistration.rider' | translate }}</label>
      <div class="position-relative">
        <div class="input-group">
          <input
            type="text"
            class="form-control"
            [(ngModel)]="userSearchTerm"
            [ngModelOptions]="{standalone: true}"
            (input)="onUserSearch()"
            [placeholder]="'editRegistration.searchPlaceholder' | translate"
            [class.is-invalid]="userIdInvalid"
          />
          @if (selectedUser) {
            <button
              class="btn btn-outline-secondary"
              type="button"
              (click)="clearUserSearch()"
            >
              <i class="fa fa-times"></i>
            </button>
          }
        </div>
        @if (userIdInvalid) {
          <div class="text-danger fs-7 mt-1">{{ 'editRegistration.riderRequired' | translate }}</div>
        }

        <!-- User Dropdown -->
        @if (userSearchResults.length > 0 && userSearchTerm && !selectedUser) {
          <div class="user-dropdown position-absolute w-100 bg-white border rounded shadow-sm mt-1" style="z-index: 1000; max-height: 300px; overflow-y: auto;">
            @for (user of userSearchResults; track user.user_id) {
              <div
                class="user-item p-3 cursor-pointer hover-bg-light border-bottom"
                (click)="selectUser(user)"
              >
                <div class="fw-semibold">{{ user.first_name }} {{ user.last_name }}</div>
                @if (user.email) {
                  <div class="text-muted fs-7">{{ user.email }}</div>
                }
              </div>
            }
          </div>
        }

        <!-- Selected User Display -->
        @if (selectedUser) {
          <div class="mt-3 p-4 bg-light rounded">
            <div class="fw-bold mb-2">{{ 'editRegistration.selectedRider' | translate }}</div>
            <div class="row g-3">
              <div class="col-md-6">
                <div class="text-muted fs-7">{{ 'editRegistration.name' | translate }}</div>
                <div class="fw-semibold">{{ selectedUser.first_name }} {{ selectedUser.last_name }}</div>
              </div>
              @if (selectedUser.email) {
                <div class="col-md-6">
                  <div class="text-muted fs-7">{{ 'editRegistration.email' | translate }}</div>
                  <div class="fw-semibold">{{ selectedUser.email }}</div>
                </div>
              }
              <div class="col-md-6">
                <div class="text-muted fs-7">{{ 'editRegistration.userId' | translate }}</div>
                <div class="fw-semibold">{{ selectedUser.user_id }}</div>
              </div>
            </div>
          </div>
        }
      </div>
    </div>

    <!-- Horse Selector -->
    <div class="mb-5">
      <div class="d-flex justify-content-between align-items-center mb-2">
        <label class="form-label required mb-0">{{ 'editRegistration.horse' | translate }}</label>
        <button
          type="button"
          class="btn btn-sm btn-light-primary"
          (click)="openAddHorseModal()"
          [disabled]="!selectedUser"
        >
          <i class="fa fa-plus me-1"></i>
          {{ 'editRegistration.addNewHorse' | translate }}
        </button>
      </div>
      @if (loadingHorses) {
        <div class="d-flex align-items-center">
          <div class="spinner-border spinner-border-sm me-2" role="status"></div>
          <span class="text-muted">{{ 'editRegistration.loadingHorses' | translate }}</span>
        </div>
      } @else {
        @if (availableHorses.length > 0) {
          <app-selectorini
            [options]="availableHorses"
            [displayKey]="'name'"
            [valueKey]="'horse_id'"
            [placeholder]="'editRegistration.selectHorse' | translate"
            [value]="form.get('horse_id')?.value"
            (valueChange)="onHorseChanged($event)"
            [searchable]="true"
          />
          @if (horseIdInvalid) {
            <div class="text-danger fs-7 mt-1">{{ 'editRegistration.horseRequired' | translate }}</div>
          }
          <div class="text-muted fs-7 mt-1">
            {{ 'editRegistration.showingHorses' | translate:{ count: availableHorses.length } }}
          </div>
          @if (selectedHorse) {
            <div class="mt-3 p-4 bg-light rounded">
              <div class="fw-bold mb-2">{{ 'editRegistration.selectedHorseDetails' | translate }}</div>
              <div class="row g-3">
                <div class="col-md-6">
                  <div class="text-muted fs-7">{{ 'editRegistration.name' | translate }}</div>
                  <div class="fw-semibold">{{ selectedHorse.name }}</div>
                </div>
                @if (selectedHorse.breed) {
                  <div class="col-md-6">
                    <div class="text-muted fs-7">{{ 'editRegistration.breed' | translate }}</div>
                    <div class="fw-semibold">{{ selectedHorse.breed }}</div>
                  </div>
                }
                @if (selectedHorse.color) {
                  <div class="col-md-6">
                    <div class="text-muted fs-7">{{ 'editRegistration.color' | translate }}</div>
                    <div class="fw-semibold">{{ selectedHorse.color }}</div>
                  </div>
                }
                @if (selectedHorse.gender) {
                  <div class="col-md-6">
                    <div class="text-muted fs-7">{{ 'editRegistration.gender' | translate }}</div>
                    <div class="fw-semibold">{{ selectedHorse.gender }}</div>
                  </div>
                }
              </div>
            </div>
          }
        } @else {
          <div class="alert alert-warning d-flex align-items-center">
            <i class="fa fa-exclamation-triangle me-2"></i>
            <div>
              @if (form.get('user_id')?.value) {
                {{ 'editRegistration.noHorsesForUser' | translate }}
              } @else {
                {{ 'editRegistration.enterUserIdFirst' | translate }}
              }
            </div>
          </div>
        }
      }
    </div>

    <!-- Notes Field -->
    <div class="mb-5">
      <label class="form-label">{{ 'editRegistration.notes' | translate }}</label>
      <textarea
        class="form-control"
        formControlName="notes"
        rows="3"
        [placeholder]="'editRegistration.notesPlaceholder' | translate"
      ></textarea>
      <div class="text-muted fs-7 mt-1">{{ 'editRegistration.notesHint' | translate }}</div>
    </div>
  </form>
</div>

<div class="modal-footer">
  <app-button
    [variant]="'outline'"
    [label]="'common.cancel' | translate"
    (clicked)="close()"
    [disabled]="saving"
  />
  <app-button
    [variant]="'primary'"
    [label]="saving ? ('common.saving' | translate) : ('common.saveChanges' | translate)"
    [iconClass]="saving ? 'fa fa-spinner fa-spin' : 'fa fa-save'"
    (clicked)="save()"
    [disabled]="saving || form.invalid"
  />
</div>
